import {
  Button,
  cn,
  Link,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@beweegbaas/ui";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { DragDropProvider } from "@dnd-kit/react";
import { useRef } from "react";
import { Page } from "#/components/page";
import { PendingContent } from "#/components/pending-content";
import { SubmitButton } from "#/components/submit-button";
import { DroppableGroup } from "#/modules/les.start-flow/components/droppable-group";
import { GroupFilters } from "#/modules/les.start-flow/components/group-filters";
import { ScheduleLessonButton } from "#/modules/les.start-flow/components/schedule-lesson-button";
import { StartLessonSummary } from "#/modules/les.start-flow/components/summary.block";
import { FALLBACK_AANTAL_GROEPEN, type StartLessonFilters } from "#/modules/les.start-flow/filters";
import { useLesgroepenDragDrop } from "#/modules/les.start-flow/hooks/use-lesgroepen-drag-drop";
import {
  getLesFlowStep3Data,
  submitLesFlowStep3,
} from "#/modules/les.start-flow/les-flow.functions";
import { useActiveLessen } from "#/modules/util.auth/use-user";
import { op } from "#/modules/util.instrumentation/op";
import { z } from "zod/v4";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/start/les-flow/3",
)({
  beforeLoad: ({ search, params, context }) => {
    // Step 3 needs at least one chosen class; without one, back to step 1.
    if (search.klasIds.length === 0) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/$school/start/les-flow/1",
        params,
        search,
      });
    }
    // Grouping is deterministic per seed + aantalGroepen; backfill both into the URL on first
    // entry so the URL is shareable and the loader cache key is stable. aantalGroepen falls
    // back to the docent's voorkeur so Gloria-style "always 3 groepjes" works without touching
    // the slider each les.
    if (!search.seed || search.aantalGroepen === undefined) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/$school/start/les-flow/3",
        params,
        search: {
          ...search,
          seed: search.seed ?? Math.random().toString(36).substring(2, 15),
          aantalGroepen:
            search.aantalGroepen ?? context.user?.defaultAantalGroepen ?? FALLBACK_AANTAL_GROEPEN,
        },
        replace: true,
      });
    }
  },
  // Narrowed to the keys getLesFlowStep3Data actually consumes — never the whole search object.
  loaderDeps: ({ search }) => ({
    klasIds: search.klasIds,
    // beforeLoad backfilled this; the fallback only narrows the type.
    aantalGroepen: search.aantalGroepen ?? FALLBACK_AANTAL_GROEPEN,
    groepeerType: search.groepeerType,
    beweegthema: search.beweegthema,
    seed: search.seed,
    lockedLeerlingen: search.lockedLeerlingen,
    excludedLeerlingen: search.excludedLeerlingen,
    beweegactiviteiten: search.beweegactiviteiten,
    editLesId: search.editLesId,
  }),
  loader: ({ deps }) => getLesFlowStep3Data({ data: deps }),
  component: LesFlowStep3Page,
});

function LesFlowStep3Page() {
  const loaderData = Route.useLoaderData();
  const { lesGroepen: initialLesGroepen, filteredBeweegthemas } = loaderData;
  const search = Route.useSearch();
  const params = Route.useParams();
  const navigate = Route.useNavigate();
  const submitStep3 = useServerFn(submitLesFlowStep3);
  const isEditing = search.editLesId != null;

  // An active les on this school blocks starten (backend `DocentAlreadyHasActiveLes`), so the
  // flow can only end in plannen. Editing an existing (planned) les is unaffected.
  const activeLessen = useActiveLessen();
  const hasActiveLes = !isEditing && activeLessen.some((les) => les.schoolSlug === params.school);

  const setFilters = (patch: Partial<StartLessonFilters>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true, resetScroll: false });

  const submit = async (formData: FormData) => {
    const intent = z.string().catch("start").parse(formData.get("intent"));
    // useServerFn resolves the server fn's success redirect into a navigation, so the await
    // returning (instead of throwing) is the success path.
    await submitStep3({
      data: {
        locale: params.locale,
        schoolyear: params.schoolyear,
        school: params.school,
        intent,
        filters: search,
      },
    });
    if (intent === "start") {
      void op.track("lesson_started", {
        source: "lesson_flow",
        group_count: search.aantalGroepen ?? FALLBACK_AANTAL_GROEPEN,
        grouping_type: search.groepeerType,
      });
    }
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const submitter = event.nativeEvent instanceof SubmitEvent ? event.nativeEvent.submitter : null;
    const formData = new FormData(event.currentTarget);
    if (submitter instanceof HTMLButtonElement && submitter.name) {
      formData.set(submitter.name, submitter.value);
    }
    void submit(formData);
  };

  const formRef = useRef<HTMLFormElement>(null);
  // Use the drag and drop hook
  const { lesGroepen, handleDragEnd } = useLesgroepenDragDrop({
    initialLesGroepen,
    setLockedLeerlingen: (updater) => {
      const updated = updater(search.lockedLeerlingen);
      void setFilters({ lockedLeerlingen: updated });
    },
  });

  return (
    <DragDropProvider onDragEnd={handleDragEnd}>
      <form ref={formRef} onSubmit={onSubmit} className="flex flex-col gap-8">
        <Page>
          <Page.Header>
            <Page.Title>
              {isEditing ? m["les.start.edit_your_groups"]() : m["les.start.create_your_groups"]()}
            </Page.Title>

            <Page.Header.Actions className="max-sm:w-full">
              {!isEditing && (
                <Page.Header.BackAction>
                  <Link
                    variant="muted"
                    size="sm"
                    className="mr-2"
                    underline={true}
                    to="/$locale/{-$schoolyear}/$school/start/les-flow/2"
                    params={params}
                    search={search}
                  >
                    {m["common.actions.back"]()}
                  </Link>
                </Page.Header.BackAction>
              )}
              <div className={"flex grow items-center gap-4"}>
                {hasActiveLes ? (
                  /* Starten is blocked while a les is active: plannen becomes the main action. */
                  <Page.Header.MainAction>
                    <ScheduleLessonButton
                      name="scheduledDate"
                      primary
                      description={m["les.start.opslaan_voor_later"]()}
                      note={m["les.start.active_les_plan_only"]()}
                      onChange={(date) => {
                        void setFilters({ scheduledDate: date.toString() });
                      }}
                      onSubmit={() => {
                        if (!formRef.current) return;
                        const formData = new FormData(formRef.current);
                        formData.set("intent", "save");
                        void submit(formData);
                      }}
                    />
                  </Page.Header.MainAction>
                ) : (
                  <>
                    {/* Shown when creating, or when editing a les whose date is still editable. */}
                    {(!isEditing || loaderData.getDocentGestuurdeLesData?.canEditDate) && (
                      <>
                        <Page.Header.SecondaryActions>
                          <ScheduleLessonButton
                            name="scheduledDate"
                            isEditing={isEditing}
                            description={m["les.start.opslaan_voor_later"]()}
                            onChange={(date) => {
                              void setFilters({ scheduledDate: date.toString() });
                            }}
                            onSubmit={() => {
                              if (!formRef.current) return;
                              const formData = new FormData(formRef.current);
                              formData.set("intent", isEditing ? "edit" : "save");
                              void submit(formData);
                            }}
                          />
                        </Page.Header.SecondaryActions>
                        <Separator
                          orientation={"vertical"}
                          className={"mx-3 -skew-x-10 self-stretch @max-[620px]/page-header:hidden"}
                        />
                      </>
                    )}
                    <Page.Header.MainAction>
                      <SubmitButton
                        type={"submit"}
                        size="xl"
                        name="intent"
                        className={"max-md:grow"}
                        value={isEditing ? "edit" : "start"}
                      >
                        {isEditing ? m["les.actief.terug_naar_de_les"]() : m["les.start.cta"]()}
                        {!isEditing && <IconMaterialSymbolsArrowRightAltRounded />}
                      </SubmitButton>
                    </Page.Header.MainAction>
                  </>
                )}
              </div>
            </Page.Header.Actions>
          </Page.Header>
          <Page.Container>
            <Page.Body>
              <Page.SidebarLayout>
                <div>
                  <GroupFilters filteredBeweegthemas={filteredBeweegthemas} />
                  <div className="mb-8 max-w-7xl">
                    <div
                      className="mb-2 flex flex-wrap items-center justify-between
                      gap-2 md:gap-4"
                    >
                      <p
                        className="text-muted-foreground flex items-start gap-1
                        text-sm"
                      >
                        <IconMaterialSymbolsInfoOutline className="mt-0.5 size-4 shrink-0" />
                        Versleep leerlingen om groepjes aan te passen
                      </p>

                      <div className="flex items-center gap-1 max-sm:grow">
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <Button
                                variant="secondary"
                                type="button"
                                className={"max-sm:grow"}
                                size="xs"
                                onClick={() => {
                                  // Unlike dragging, a shuffle may move everyone: "doet niet mee"
                                  // leaves the verdeling so the rest is spread evenly.
                                  const inGroep = new Set(
                                    search.lockedLeerlingen
                                      .filter((l) => l.groep !== -1)
                                      .map((l) => l.leerlingId),
                                  );
                                  const doetNietMee = search.lockedLeerlingen
                                    .filter((l) => l.groep === -1)
                                    .map((l) => l.leerlingId);
                                  void setFilters({
                                    lockedLeerlingen: [],
                                    excludedLeerlingen: [
                                      ...new Set([...search.excludedLeerlingen, ...doetNietMee]),
                                    ].filter((id) => !inGroep.has(id)),
                                    seed: Math.random().toString(),
                                  });
                                }}
                              >
                                <IconMaterialSymbolsShuffleRounded />
                                Shuffle
                              </Button>
                            }
                          />
                          <TooltipContent>
                            Verdeel opnieuw de groepjes, rekening houdend met de groepeer- en
                            differentiatie instellingen
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                    {/* Dims the groups while the loader re-runs (slider drag, shuffle, filters). */}
                    <PendingContent className="@container/groepgrid min-w-0">
                      <div
                        className={cn("grid w-full min-w-0 grid-cols-1 gap-1.5 md:gap-2", {
                          "@min-[380px]/groepgrid:grid-cols-2 2xl:grid-cols-3":
                            lesGroepen.lesgroepen.length >= 3,
                          "@min-[380px]/groepgrid:grid-cols-2": lesGroepen.lesgroepen.length === 2,
                        })}
                      >
                        {lesGroepen.lesgroepen.slice(1).map((group) => (
                          <DroppableGroup
                            key={group.groep}
                            group={group}
                            groepeerType={search.groepeerType}
                            totalGroups={lesGroepen.lesgroepen.length}
                          />
                        ))}
                        {lesGroepen.lesgroepen[0] && (
                          <DroppableGroup
                            group={lesGroepen.lesgroepen[0]} // "Doet niet mee" group
                            groepeerType={search.groepeerType}
                            isDoetNietMee={true}
                            totalGroups={lesGroepen.lesgroepen.length}
                          />
                        )}
                      </div>
                    </PendingContent>
                  </div>
                </div>
                <Page.Sidebar className="@max-4xl/sidebar-layout:hidden @6xl/sidebar-layout:max-w-[320px]">
                  <StartLessonSummary className={"sticky top-4"} />
                </Page.Sidebar>
              </Page.SidebarLayout>
            </Page.Body>
          </Page.Container>
        </Page>
      </form>
    </DragDropProvider>
  );
}
